import React, { useState, useEffect } from "react";
import { SelectionState, DayFlag, UI, getDefaultClassNames, DayPicker, MonthCaption } from "react-day-picker";
import "react-day-picker/style.css";
import { Box } from "@mui/material";
import { format } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { zhCN } from "date-fns/locale/zh-CN";
import { makeStyles } from "tss-react/mui";
import "../button/Buttons.js";
import "../button/EditButton.js";
import { withDropdown } from "../dropdown/DropdownHoc.js";
import "../dropdown/DropdownWithChip.js";
import "../dropdown/DropdownItem.js";
import { LocalizedText } from "../i18n/LocalizedText.js";
import { Icon, IconColor } from "../icons/Icon.js";
import { CustomIcon } from "../icons/types.js";
import "../icons/ExpandMoreIcon.js";
import { StyledTooltip } from "../tooltip/Tooltip.js";
import { getTodayAsDate, getEndOfFutureYear, formatDayOfWeek } from "../../utils/date.js";
import "react-intl";
import "../label/EmptyLabel.js";
import "../label/LabelHoc.js";
import "../tooltip/AutoAdjustedWidthTooltip.styles.js";
import "../tooltip/AutoAdjustedComplexTooltip.styles.js";
import "../tooltip/MultiLineAutoAdjustedWidthTooltip.js";
import "../../localization/MessageLocalizer.js";
import { useLocale } from "../../localization/hooks.js";
import "../../localization/LocaleProvider.js";
import "../../localization/sharedResources.js";
import { localized } from "./DatePicker.localize.js";
import { DatePickerDropdown } from "./DatePickerDropdown.js";
import { DatePickerFooterRangeMode } from "./DatePickerFooterRangeMode.js";
import { DatePickerFooterSingleMode } from "./DatePickerFooterSingleMode.js";
import { PreviousMonthButton, NextMonthButton } from "./DatePickerNavButtons.js";
import { StyledInput } from "./StyledInput.js";
const localeMapping = { en: enUS, "zh-CN": zhCN };
const useDayPickerStyles = makeStyles()((theme, { type }, classes) => {
  const cellPadding = parseInt(theme.spacing(0.75), 10);
  const cellSize = parseInt(theme.spacing(4), 10);
  const isLarge = type === "large";
  const fillerStyles = {
    content: '""',
    position: "absolute",
    top: cellPadding,
    width: "100%",
    height: cellSize,
    backgroundColor: theme.palette.primary.light,
    zIndex: -1
  };
  return {
    [UI.Root]: {
      ...isLarge ? {
        maxWidth: "min-content",
        marginBottom: theme.spacing(2)
      } : {
        [theme.breakpoints.down("sm")]: {
          margin: theme.spacing(0.5),
          maxWidth: "unset"
        }
      }
    },
    [UI.MonthCaption]: {
      width: "100%",
      flexGrow: 1
    },
    [UI.Nav]: {
      gap: theme.spacing(1)
    },
    [UI.Weekday]: {
      padding: theme.spacing(isLarge ? 2.5 : 2, 0, 1),
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.caption.fontWeight,
      color: theme.palette.text.secondary
    },
    [UI.Dropdowns]: {
      width: "100%",
      display: "grid",
      gridTemplateColumns: "1fr 0.9fr",
      gap: theme.spacing(1),
      alignItems: "center",
      paddingRight: "70px"
      // for navigation buttons with absolute positioning
    },
    [UI.MonthGrid]: {
      width: "100%",
      ...!isLarge ? { margin: theme.spacing(0, -1.5) } : {}
    },
    [UI.Day]: {
      position: "relative",
      padding: theme.spacing(0.75),
      [`& > button`]: {
        border: 0,
        width: cellSize,
        height: cellSize,
        margin: "auto",
        "&:hover": {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.main
        }
      }
    },
    [DayFlag.today]: {
      fontWeight: "bold"
    },
    [DayFlag.outside]: {
      color: theme.palette.text.secondary,
      "&:hover": {
        color: theme.palette.primary.main
      }
    },
    [DayFlag.disabled]: {
      [`& > button, & > button:hover`]: {
        color: theme.palette.text.secondary,
        opacity: "0.25",
        backgroundColor: "transparent"
      }
    },
    [SelectionState.range_start]: {
      [`&:after`]: {
        ...fillerStyles,
        right: -cellPadding,
        width: "50%"
      },
      [`&.${classes.range_end}`]: {
        [`&:after`]: {
          display: "none"
        }
      }
    },
    [SelectionState.range_end]: {
      [`&:after`]: {
        ...fillerStyles,
        left: -cellPadding,
        right: "unset",
        width: "50%"
      }
    },
    [SelectionState.selected]: {
      [`& > button, & > button:hover`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        position: "relative"
      }
    },
    [SelectionState.range_middle]: {
      [`&:after`]: {
        ...fillerStyles,
        right: -cellPadding
      },
      [`& > button, & > button:hover`]: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main
      },
      [`&:last-of-type`]: {
        [`&:after`]: {
          width: "50%",
          left: -cellPadding,
          right: "unset"
        }
      },
      [`&:first-of-type`]: {
        [`&:after`]: {
          width: "50%"
        }
      }
    }
  };
});
const useStyles = makeStyles()((theme) => ({
  datePickerInputFillAvailableWidth: {
    width: "100%"
  },
  calendarIcon: {
    color: theme.palette.secondary.main,
    fontSize: 15
  },
  calendarIconOpen: {
    color: theme.palette.primary.main
  },
  calendarIconStart: {
    paddingRight: theme.spacing(2)
  },
  calendarIconEnd: {
    paddingLeft: theme.spacing(2)
  },
  datePickerContainer: {
    padding: theme.spacing(2)
  }
}));
const StyledInputDropdown = withDropdown(StyledInput);
const DatePicker = ({
  captionComponent,
  displayMode = "desktop",
  isOpen,
  value,
  formattedValue,
  defaultMonthDate,
  isDateSelected,
  disabledDateMatcher,
  disableFutureDates = false,
  onDropdownOpen,
  onDropdownClose,
  onSelect,
  isApplyDisabled,
  onApplyClick,
  onCancelClick,
  onOutsideClick,
  isRangeMode,
  placeholder,
  className,
  applyAction = true,
  cancelAction = true,
  footer,
  iconPlacement = "start",
  type = "large",
  inputTooltipMessage,
  disabled,
  ...textInputProps
}) => {
  const { classes } = useStyles();
  const { cx, classes: dayPickerClasses } = useDayPickerStyles({ type });
  const defaultClasses = getDefaultClassNames();
  const locale = useLocale()?.locale;
  const isMobile = displayMode === "mobile";
  const calendarIconPlacementClass = iconPlacement === "start" ? classes.calendarIconStart : classes.calendarIconEnd;
  const calendarIcon = isOpen ? `${classes.calendarIcon} ${classes.calendarIconOpen}` : classes.calendarIcon;
  const calendarIconComponent = /* @__PURE__ */ React.createElement(Box, { component: "span", className: `${CustomIcon.CalendarOutline} ${calendarIcon}  ${calendarIconPlacementClass}` });
  const [currentDate, setCurrentDate] = useState(defaultMonthDate ? defaultMonthDate : getTodayAsDate());
  const handleYearMonthChange = (date) => {
    setCurrentDate(date);
  };
  const toDate = disableFutureDates ? getTodayAsDate() : null;
  const endMonth = toDate ?? getEndOfFutureYear();
  const selectedDate = isRangeMode ? /* @__PURE__ */ React.createElement(DatePickerFooterRangeMode, { value, locale, onChange: onSelect }) : /* @__PURE__ */ React.createElement(DatePickerFooterSingleMode, { value, locale, onChange: onSelect });
  function getDisabledDates() {
    return [disabledDateMatcher, disableFutureDates ? { after: toDate } : null].filter(Boolean).flat();
  }
  const Caption = captionComponent;
  const datePickerLocale = localeMapping[locale];
  const rangeDayPicker = /* @__PURE__ */ React.createElement(
    DayPicker,
    {
      onSelect,
      mode: "range",
      locale: datePickerLocale,
      defaultMonth: defaultMonthDate,
      showOutsideDays: true,
      month: currentDate,
      onMonthChange: handleYearMonthChange,
      selected: value,
      disabled: getDisabledDates(),
      endMonth,
      today: getTodayAsDate(),
      components: {
        MonthCaption: captionComponent ? ({ calendarMonth }) => /* @__PURE__ */ React.createElement(Caption, { date: calendarMonth.date }) : MonthCaption,
        NextMonthButton,
        PreviousMonthButton,
        Dropdown: DatePickerDropdown
      },
      footer: /* @__PURE__ */ React.createElement(React.Fragment, null, selectedDate, footer),
      captionLayout: "dropdown",
      navLayout: "after",
      classNames: {
        [UI.Root]: cx(defaultClasses[UI.Root], dayPickerClasses[UI.Root]),
        [UI.Nav]: cx(defaultClasses[UI.Nav], dayPickerClasses[UI.Nav]),
        [UI.Dropdowns]: cx(dayPickerClasses[UI.Dropdowns]),
        [UI.MonthCaption]: cx(defaultClasses[UI.MonthCaption], dayPickerClasses[UI.MonthCaption]),
        [UI.Weekday]: cx(dayPickerClasses[UI.Weekday]),
        [UI.MonthGrid]: cx(defaultClasses[UI.MonthGrid], dayPickerClasses[UI.MonthGrid]),
        [UI.Day]: cx(defaultClasses[UI.Day], dayPickerClasses[UI.Day]),
        [DayFlag.today]: cx(dayPickerClasses[DayFlag.today]),
        [DayFlag.outside]: cx(dayPickerClasses[DayFlag.outside]),
        [DayFlag.disabled]: cx(dayPickerClasses[DayFlag.disabled]),
        [SelectionState.range_start]: cx(dayPickerClasses[SelectionState.range_start]),
        [SelectionState.range_middle]: cx(dayPickerClasses[SelectionState.range_middle]),
        [SelectionState.range_end]: cx(dayPickerClasses[SelectionState.range_end]),
        [SelectionState.selected]: cx(dayPickerClasses[SelectionState.selected])
      },
      formatters: {
        formatWeekdayName: (date) => format(date, "EEE", { locale: datePickerLocale })
      }
    }
  );
  const singleDayPicker = /* @__PURE__ */ React.createElement(
    DayPicker,
    {
      onSelect,
      mode: "single",
      locale: datePickerLocale,
      defaultMonth: defaultMonthDate,
      showOutsideDays: true,
      month: currentDate,
      onMonthChange: handleYearMonthChange,
      selected: value,
      endMonth,
      disabled: getDisabledDates(),
      formatters: {
        formatWeekdayName: (date) => formatDayOfWeek(date, "ddd")
      },
      today: getTodayAsDate(),
      classNames: {
        [UI.Root]: cx(defaultClasses[UI.Root], dayPickerClasses[UI.Root]),
        [UI.Nav]: cx(defaultClasses[UI.Nav], dayPickerClasses[UI.Nav]),
        [UI.Dropdowns]: cx(dayPickerClasses[UI.Dropdowns]),
        [UI.MonthCaption]: cx(defaultClasses[UI.MonthCaption], dayPickerClasses[UI.MonthCaption]),
        [UI.Weekday]: cx(dayPickerClasses[UI.Weekday]),
        [UI.MonthGrid]: cx(defaultClasses[UI.MonthGrid], dayPickerClasses[UI.MonthGrid]),
        [UI.Day]: cx(defaultClasses[UI.Day], dayPickerClasses[UI.Day]),
        [DayFlag.today]: cx(dayPickerClasses[DayFlag.today]),
        [DayFlag.outside]: cx(dayPickerClasses[DayFlag.outside]),
        [DayFlag.disabled]: cx(dayPickerClasses[DayFlag.disabled]),
        [SelectionState.range_start]: cx(dayPickerClasses[SelectionState.range_start]),
        [SelectionState.range_middle]: cx(dayPickerClasses[SelectionState.range_middle]),
        [SelectionState.range_end]: cx(dayPickerClasses[SelectionState.range_end]),
        [SelectionState.selected]: cx(dayPickerClasses[SelectionState.selected])
      },
      components: {
        MonthCaption: captionComponent ? ({ calendarMonth }) => /* @__PURE__ */ React.createElement(Caption, { date: calendarMonth.date }) : MonthCaption,
        NextMonthButton,
        PreviousMonthButton,
        Dropdown: DatePickerDropdown
      },
      captionLayout: "dropdown",
      navLayout: "after",
      footer: /* @__PURE__ */ React.createElement(React.Fragment, null, selectedDate, footer)
    }
  );
  const dayPicker = isRangeMode ? rangeDayPicker : singleDayPicker;
  useEffect(() => {
    handleYearMonthChange(defaultMonthDate ? defaultMonthDate : getTodayAsDate());
  }, [defaultMonthDate?.getMonth(), defaultMonthDate?.getFullYear()]);
  return isMobile ? dayPicker : /* @__PURE__ */ React.createElement(
    StyledInputDropdown,
    {
      className: `${classes.datePickerInputFillAvailableWidth} ${className}`,
      "data-testid": "dateRangePicker",
      value: formattedValue,
      onChange: () => {
      },
      startAdornment: iconPlacement === "start" ? calendarIconComponent : null,
      endAdornment: /* @__PURE__ */ React.createElement(React.Fragment, null, inputTooltipMessage ? /* @__PURE__ */ React.createElement(
        StyledTooltip,
        {
          title: /* @__PURE__ */ React.createElement(LocalizedText, { label: inputTooltipMessage }),
          element: /* @__PURE__ */ React.createElement(Icon, { color: IconColor.Secondary, icon: CustomIcon.WarningOutline })
        }
      ) : null, iconPlacement === "end" ? calendarIconComponent : null),
      buttonContent: "",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      closeOnClickInside: false,
      closeOnClickOutside: true,
      applyAction,
      cancelAction: isDateSelected && cancelAction ? localized.datePickerClearLabel : cancelAction,
      onApplyClick,
      isApplyDisabled,
      onCancelClick,
      onOutsideClick,
      onDropdownOpen,
      onDropdownClose,
      styles: {
        dropdownContainer: classes.datePickerContainer
      },
      placeholder,
      disabled,
      ...textInputProps
    },
    dayPicker
  );
};
export {
  DatePicker
};
//# sourceMappingURL=DatePicker.js.map
